import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Sparkle, Star, ArrowRight, CheckCircle, Clock, ClipboardText } from "@phosphor-icons/react";
import { stageEnteredLabel } from "@/lib/stageHistory";
import IntakeTab from "@/components/IntakeTab";
import { resolveAiResponse } from "@/hooks/useAiJob";

/**
 * Recruiter-side screening panel inside CandidateDrawer.
 * Visible when the candidate is in "Screening" stage.
 *
 * V1.5.6 — Now houses two tabs:
 *   • Intake form (generic mandatory intake questions — default tab)
 *   • AI screening (existing star-rated Q&A flow)
 * Compact segmented tab strip at the top; only one modality visible at a
 * time to keep the drawer clutter-free.
 */
export default function ScreeningPanel({ candidate, job, onJobChanged, onCandidateChanged }) {
    const [tab, setTab] = useState("intake"); // intake | ai
    // Track intake completeness so we can render a dot on the tab label.
    // We look at candidate.intake_response.answers vs job's template fields.
    const intakeFilled = React.useMemo(() => {
        const ans = ((candidate?.intake_response || {}).answers) || {};
        return Object.keys(ans).length > 0;
    }, [candidate?.intake_response]);

    return (
        <div data-testid="screening-panel-wrapper">
            {/* Segmented tabs */}
            <div className="flex items-center gap-1 mb-3" role="tablist" data-testid="screening-tabs">
                <TabButton
                    active={tab === "intake"}
                    onClick={() => setTab("intake")}
                    testId="screening-tab-intake"
                    icon={<ClipboardText size={12} weight="fill" />}
                    label="Intake form"
                    dot={intakeFilled ? "green" : "amber"}
                />
                <TabButton
                    active={tab === "ai"}
                    onClick={() => setTab("ai")}
                    testId="screening-tab-ai"
                    icon={<Sparkle size={12} weight="fill" />}
                    label="AI screening"
                    dot={candidate?.screening_avg != null ? "green" : null}
                />
            </div>

            {tab === "intake" ? (
                <IntakeTab candidate={candidate} job={job}
                           onCandidateChanged={onCandidateChanged} />
            ) : (
                <AIScreeningTab
                    candidate={candidate}
                    job={job}
                    onJobChanged={onJobChanged}
                    onCandidateChanged={onCandidateChanged}
                    switchToIntake={() => setTab("intake")}
                />
            )}
        </div>
    );
}

function TabButton({ active, onClick, label, icon, testId, dot }) {
    const dotColor = dot === "green" ? "bg-emerald-500"
                   : dot === "amber" ? "bg-amber-400"
                   : dot === "blue"  ? "bg-cobalt"
                   : "bg-transparent";
    return (
        <button
            role="tab"
            aria-selected={active}
            data-testid={testId}
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.15em] border transition-colors ${
                active
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-[#525252] border-[#E5E5E5] hover:border-ink"
            }`}
        >
            {icon}
            {label}
            {dot && <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />}
        </button>
    );
}

function AIScreeningTab({ candidate, job, onJobChanged, onCandidateChanged, switchToIntake }) {
    const [generating, setGenerating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const questions = job?.screening_questions || [];
    const existingScores = candidate?.screening_scores || [];
    const existingById = Object.fromEntries(existingScores.map((s) => [s.question_id, s]));
    const alreadySubmitted = existingScores.length > 0;

    // Local form state: { [question_id]: { rating, comment } }
    const [form, setForm] = useState(() => {
        const initial = {};
        questions.forEach((q) => {
            const prev = existingById[q.id];
            initial[q.id] = { rating: prev?.rating || 0, comment: prev?.comment || "" };
        });
        return initial;
    });

    useEffect(() => {
        const initial = {};
        questions.forEach((q) => {
            const prev = existingById[q.id];
            initial[q.id] = { rating: prev?.rating || 0, comment: prev?.comment || "" };
        });
        setForm(initial);
    }, [candidate?.id, questions.length, existingScores.length]);

    const generate = async () => {
        setErr("");
        setGenerating(true);
        try {
            const r = await api.post(`/jobs/${job.id}/screening-questions/generate`);
            // V1.9.0 — async job pattern (see /app/frontend/src/hooks/useAiJob.js).
            const data = await resolveAiResponse(r.data);
            onJobChanged?.({ ...job, screening_questions: data.questions });
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setGenerating(false);
        }
    };

    const submit = async () => {
        setErr("");
        const ratings = Object.entries(form)
            .filter(([, v]) => v.rating > 0)
            .map(([qid, v]) => ({
                question_id: qid,
                rating: v.rating,
                comment: v.comment,
            }));
        if (ratings.length === 0) {
            setErr("Rate at least one question before submitting.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post(`/candidates/${candidate.id}/screening`, { ratings });
            // Backend returns `{ok, avg, advanced, new_stage, intake_blocked, ...}` —
            // NOT the full candidate. Use `advanced` + `new_stage` from the
            // response, not the old (buggy) `r.data.stage` read which was
            // always undefined and made the "advanced" toast never fire.
            const advanced = !!r.data?.advanced && r.data?.new_stage === "Interview";
            // V1.5.6 — auto-advance may be blocked by the intake gate.
            if (r.data?.intake_blocked) {
                toast.warning("Screening submitted — advance blocked", {
                    description: `Complete the intake form (${(r.data.intake_missing || []).length} required fields left) to auto-advance.`,
                });
            } else {
                toast.success(
                    advanced ? "Screening submitted — candidate advanced to Interview" : "Screening submitted",
                    {
                        description: r.data?.avg != null
                            ? `Average score: ${r.data.avg}/5`
                            : undefined,
                    },
                );
            }
            onCandidateChanged?.(r.data);
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSubmitting(false);
        }
    };

    const ratedCount = Object.values(form).filter((v) => v.rating > 0).length;
    const avgIfNow = ratedCount
        ? Object.values(form).reduce((a, v) => a + v.rating, 0) / ratedCount
        : 0;

    return (
        <div
            data-testid="screening-panel"
            className="border-2 border-cobalt p-5"
        >
            {stageEnteredLabel(candidate?.stage_history, "Screening") && (
                <p
                    data-testid="screening-entered-at"
                    className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 inline-flex items-center gap-1"
                >
                    <Clock size={10} weight="bold" />
                    {stageEnteredLabel(candidate?.stage_history, "Screening")}
                </p>
            )}
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-cobalt">
                        AI screening · stage gate
                    </p>
                    <p className="text-xs text-[#525252] mt-0.5">
                        Avg ≥ 3.5 auto-advances to Interview (if intake form is complete)
                    </p>
                </div>
                {questions.length === 0 ? (
                    <button
                        onClick={generate}
                        disabled={generating}
                        data-testid="generate-questions-button"
                        className="bg-ink hover:bg-cobalt disabled:opacity-60 text-white px-4 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-2 transition-colors"
                    >
                        <Sparkle size={12} weight="fill" />
                        {generating ? "Generating…" : "Generate with AI"}
                    </button>
                ) : (
                    <button
                        onClick={generate}
                        disabled={generating}
                        data-testid="regenerate-questions-button"
                        className="border border-[#E5E5E5] hover:border-ink text-[#525252] px-3 py-1.5 text-[10px] font-bold tracking-wider uppercase flex items-center gap-1 transition-colors"
                    >
                        <Sparkle size={10} weight="fill" />
                        {generating ? "Re-generating…" : "Re-generate"}
                    </button>
                )}
            </div>

            {candidate.screening_avg != null && (
                <div
                    data-testid="screening-existing-avg"
                    className="bg-[#F9F9F9] border border-[#E5E5E5] p-3 mb-3 flex items-center justify-between"
                >
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                            Previous submission
                        </p>
                        <p className="text-sm font-bold text-ink">
                            Avg {candidate.screening_avg}/5 ·{" "}
                            {candidate.screening_completed_by || "—"}
                        </p>
                    </div>
                    {candidate.screening_avg >= 3.5 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-[#059669]">
                            <CheckCircle size={12} weight="fill" /> Passed
                        </span>
                    )}
                </div>
            )}

            {questions.length === 0 ? (
                <p className="text-sm text-[#525252]">
                    No screening questions yet. Click <strong>Generate with AI</strong> to create
                    five tailored questions based on the JD.
                </p>
            ) : (
                <>
                    <div className="space-y-4 mb-4">
                        {questions.map((q, idx) => {
                            const cur = form[q.id] || { rating: 0, comment: "" };
                            return (
                                <div
                                    key={q.id}
                                    data-testid={`screening-q-${idx}`}
                                    className="border border-[#E5E5E5] p-3"
                                >
                                    <p className="text-xs font-bold text-ink mb-2">
                                        <span className="text-[#A3A3A3]">
                                            Q{(idx + 1).toString().padStart(2, "0")} ·
                                        </span>{" "}
                                        {q.question}
                                    </p>
                                    <div className="flex items-center gap-1 mb-2">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <button
                                                key={n}
                                                type="button"
                                                disabled={alreadySubmitted}
                                                onClick={() =>
                                                    setForm((f) => ({
                                                        ...f,
                                                        [q.id]: { ...cur, rating: n },
                                                    }))
                                                }
                                                data-testid={`screening-q-${idx}-rating-${n}`}
                                                className={`${
                                                    n <= cur.rating
                                                        ? "text-[#F59E0B]"
                                                        : "text-[#E5E5E5] hover:text-[#A3A3A3]"
                                                } ${alreadySubmitted ? "cursor-default" : ""}`}
                                                aria-label={`Rate ${n}`}
                                            >
                                                <Star size={20} weight="fill" />
                                            </button>
                                        ))}
                                        {cur.rating > 0 && (
                                            <span className="text-xs font-bold text-[#525252] ml-2">
                                                {cur.rating}/5
                                            </span>
                                        )}
                                    </div>
                                    <input
                                        value={cur.comment}
                                        disabled={alreadySubmitted}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                [q.id]: { ...cur, comment: e.target.value },
                                            }))
                                        }
                                        placeholder={alreadySubmitted ? "" : "Optional comment…"}
                                        data-testid={`screening-q-${idx}-comment`}
                                        className="w-full bg-white border border-[#E5E5E5] px-3 py-1.5 text-xs focus:outline-none focus:border-cobalt disabled:bg-[#FAFAFA] disabled:text-[#525252]"
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {err && (
                        <div
                            data-testid="screening-error"
                            className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-xs px-3 py-2 mb-3"
                        >
                            {err}
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#E5E5E5]">
                        <p className="text-xs text-[#525252]">
                            {ratedCount}/{questions.length} rated
                            {ratedCount > 0 && (
                                <>
                                    {" "}
                                    · avg{" "}
                                    <span
                                        className={`font-bold ${
                                            avgIfNow >= 3.5 ? "text-[#059669]" : "text-[#F59E0B]"
                                        }`}
                                    >
                                        {avgIfNow.toFixed(1)}
                                    </span>
                                </>
                            )}
                        </p>
                        <button
                            onClick={submit}
                            disabled={submitting || ratedCount === 0 || alreadySubmitted}
                            data-testid="submit-screening-button"
                            className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-2 transition-colors"
                        >
                            {alreadySubmitted ? (
                                <>
                                    <CheckCircle size={12} weight="fill" /> Submitted
                                </>
                            ) : submitting ? (
                                "Submitting…"
                            ) : (
                                <>
                                    Submit &amp; advance
                                    <ArrowRight size={12} weight="bold" />
                                </>
                            )}
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={switchToIntake}
                        className="mt-2 text-[10px] font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark"
                    >
                        Advance blocked? Check intake form ↗
                    </button>
                </>
            )}
        </div>
    );
}
